import type { ApiClient } from '../client'
import type {
  AcceptOpeningResponse,
  ActionContext,
  ActionTransitionResult,
  CompleteBookingResult,
  DeclineResult,
} from '../types'

/**
 * Customer action links — src/modules/auth/controllers/actions.controller.ts. Unauthenticated:
 * the signed token in the path IS the credential, so every call is `auth: false` and nothing here
 * ever triggers a staff refresh. `GET` does not consume the token; every `POST` does.
 *
 * ★ `accept-opening` returns a HOLD (10 min) plus a single-use `completion_token`; only
 *   `POST /actions/holds/complete { token }` turns the hold into a booking (RecoupSlots P6). The UI
 *   never presents a hold as a booking — only that call's 200 does.
 */
const NO_RETRY = {
  rateLimited: { attempts: 0, maxWaitMs: 0 },
  capacity: { attempts: 0, baseDelayMs: 0 },
  network: { attempts: 0, delayMs: 0 },
}

export function actionsEndpoints(api: ApiClient) {
  const path = (token: string, suffix = '') => `actions/${encodeURIComponent(token)}${suffix}`
  return {
    context: (token: string) => api.get<ActionContext>(path(token), { auth: false }),
    confirm: (token: string) =>
      api.post<ActionTransitionResult>(path(token, '/confirm'), undefined, {
        auth: false,
        retry: NO_RETRY,
      }),
    cancel: (token: string) =>
      api.post<ActionTransitionResult>(path(token, '/cancel'), undefined, {
        auth: false,
        retry: NO_RETRY,
      }),
    acceptOpening: (token: string) =>
      api.post<AcceptOpeningResponse>(path(token, '/accept-opening'), undefined, {
        auth: false,
        retry: NO_RETRY,
      }),
    /** The fixed route with the completion token in the BODY (never in the path or a query). */
    completeHold: (completionToken: string) =>
      api.post<CompleteBookingResult>(
        'actions/holds/complete',
        { token: completionToken },
        { auth: false, retry: NO_RETRY },
      ),
    declineOpening: (token: string) =>
      api.post<DeclineResult>(path(token, '/decline-opening'), undefined, {
        auth: false,
        retry: NO_RETRY,
      }),
  }
}
